import { useState } from 'react';
import PageHero from '@/components/feature/PageHero';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { activitiesHero, activitiesList, activityCategories } from '@/mocks/activities';

export default function ActivitiesPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? activitiesList
    : activitiesList.filter((a) => a.category === activeCategory);

  const upcoming = filtered.filter((a) => a.status === 'upcoming');
  const past = filtered.filter((a) => a.status === 'past');

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        title={activitiesHero.title}
        subtitle={activitiesHero.subtitle}
        imageUrl={activitiesHero.imageUrl}
        breadcrumb={{ label: 'Home', href: '/' }}
      />

      {/* Category Filter */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 justify-center">
            {activityCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcoming.length > 0 && (
        <section className="pb-6 md:pb-8">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              Upcoming Events
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((activity) => (
                <ActivityCard key={activity.id} activity={activity} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Events */}
      {past.length > 0 && (
        <section className="py-10 md:py-14">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              Past Events
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((activity) => (
                <ActivityCard key={activity.id} activity={activity} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14 md:py-16 bg-secondary-500">
        <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            Want to Get Involved?
          </h2>
          <p className="text-background-50/80 text-base mb-8 leading-relaxed">
            Whether you want to volunteer, donate, or participate in our activities, we would love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:insanperwirapenang@gmail.com"
              className="px-6 py-3 rounded-full bg-background-50 text-secondary-700 font-medium text-sm hover:bg-background-100 transition-all whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ActivityCard({ activity }: { activity: typeof activitiesList[0] }) {
  const isUpcoming = activity.status === 'upcoming';

  return (
    <article className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group transition-all duration-300 hover:border-primary-300/50">
      <div className="relative h-48 overflow-hidden">
        <img
          src={activity.imageUrl}
          alt={activity.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
            isUpcoming
              ? 'bg-primary-500 text-background-50'
              : 'bg-background-50/90 text-foreground-700'
          }`}>
            {isUpcoming ? 'Upcoming' : 'Completed'}
          </span>
        </div>
        <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-background-50/90 text-foreground-700 text-xs font-medium whitespace-nowrap">
          {activity.category}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
          {activity.title}
        </h3>
        <div className="flex flex-col gap-1.5 mb-3">
          <div className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center"></i>
            <span>{activity.date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-time-line text-sm w-4 h-4 flex items-center justify-center"></i>
            <span>{activity.time}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-map-pin-line text-sm w-4 h-4 flex items-center justify-center"></i>
            <span>{activity.location}</span>
          </div>
        </div>
        <p className="text-foreground-600 text-sm leading-relaxed mb-4">
          {activity.description}
        </p>
        <div className="flex flex-wrap gap-2">
          {activity.highlights.map((h, i) => (
            <span key={i} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
              {h}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}